import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { RestrictedCheckStatus } from '@typesApp/Customer'
import { InsuranceMemberStep } from '@typesApp/InsuranceMember'
import { APPLY_INSURANCE } from '@constants/index'
import { isCustomerChangedError, isResourceNotFoundError } from '@utils/errorUtil'

export type InsuranceMemberResumeService = {
  fetchRestrictedStatus: (customerNumber: string) => Promise<RestrictedCheckStatus>
  fetchClaimRecord: (id: string) => Promise<ClaimRecord | null>
  onCustomerChangedError: () => Promise<void>
  onResourceNotFoundError: () => Promise<void>
}

type InsuranceMemberResumeState = {
  step: InsuranceMemberStep | undefined
  customerAttemptsLeft: number
  claimRecord?: ClaimRecord
}

type InsuranceMemberResumeControllerReturn = {
  initializeResumeState: (claimRecordId: string | undefined) => Promise<InsuranceMemberResumeState>
}

const DEFAULT_STATE: InsuranceMemberResumeState = {
  step: InsuranceMemberStep.FIND_MEMBER,
  customerAttemptsLeft: APPLY_INSURANCE.MAX_ATTEMPTS,
}

export const insuranceMemberResumeController = (
  service: InsuranceMemberResumeService
): InsuranceMemberResumeControllerReturn => {
  const initializeResumeState = async (claimRecordId: string | undefined): Promise<InsuranceMemberResumeState> => {
    if (!claimRecordId) {
      return DEFAULT_STATE
    }

    try {
      const claimRecord = await service.fetchClaimRecord(claimRecordId)
      if (!claimRecord) {
        return DEFAULT_STATE
      }

      const step = determineInsuranceMemberStep(claimRecord.status)
      const isCheckRestricted = step === InsuranceMemberStep.FIND_MEMBER
      const customerAttemptsLeft = isCheckRestricted
        ? await fetchCustomerAttemptsLeft(claimRecord.customerNumber)
        : APPLY_INSURANCE.MAX_ATTEMPTS

      return { step, customerAttemptsLeft: customerAttemptsLeft, claimRecord }
    } catch (error) {
      return handleError(error)
    }
  }

  const determineInsuranceMemberStep = (claimRecordStatus: ClaimRecordStatus | undefined) => {
    if (!claimRecordStatus) return InsuranceMemberStep.FIND_MEMBER

    switch (claimRecordStatus) {
      case ClaimRecordStatus.INSURANCE_MEMBER_FOUND:
      case ClaimRecordStatus.NO_BENEFITS_AVAILABLE:
      case ClaimRecordStatus.READY_TO_QUOTE:
      case ClaimRecordStatus.READY_TO_CLAIM:
      case ClaimRecordStatus.CLAIM_ACCEPTED:
      case ClaimRecordStatus.QUOTE_REQUIRED:
        return InsuranceMemberStep.MEMBER_FOUND
      case ClaimRecordStatus.QUOTE_REQUESTED:
        return InsuranceMemberStep.APPLY_INSURANCE
      case ClaimRecordStatus.RESTRICTED:
        return InsuranceMemberStep.RESTRICTED
      case ClaimRecordStatus.INSURANCE_MEMBER_NOT_FOUND:
      case ClaimRecordStatus.INSURANCE_MEMBER_REQUIRED:
      case ClaimRecordStatus.MULTIPLE_INSURANCE_MEMBERS_FOUND:
      default:
        return InsuranceMemberStep.FIND_MEMBER
    }
  }

  const fetchCustomerAttemptsLeft = async (customerNumber: string): Promise<number> => {
    const restrictedStatus = await service.fetchRestrictedStatus(customerNumber)
    return (
      restrictedStatus?.customer?.customerAttempts?.FIND_INSURANCE_MEMBER?.customerAttemptsLeft ??
      APPLY_INSURANCE.MAX_ATTEMPTS
    )
  }

  const handleError = async (error: unknown): Promise<InsuranceMemberResumeState> => {
    if (isCustomerChangedError(error)) {
      await service.onCustomerChangedError()
      return DEFAULT_STATE
    } else if (isResourceNotFoundError(error)) {
      await service.onResourceNotFoundError()
      return DEFAULT_STATE
    } else {
      return { ...DEFAULT_STATE, step: undefined }
    }
  }

  return {
    initializeResumeState,
  }
}
